import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { ARTICLE_CALLOUT_ATTRIBUTE, Color, DEFAULT_FONT_POINT_SIZE, fill, FONT_POINT_SIZES, LEGACY_FONT_SIZE_POINTS, LINE_SPACINGS, Size, foldedGroups, fontPointSizeOf, fontPointValue, t, type ArticleCalloutKind, type FontPointSize, type IconMeaning, type LegacyFontSize, type LineSpacing, sanitizeRichText } from '@bms2/shared';
import { sanitizeArticleSurface, sanitizeCoreSurface, sanitizeEmailSurface } from './richTextSanitize';
import { Icon } from '../Icon';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { NumericInput } from './NumericInput';
import { TextInput } from './TextInput';
import { ListBar, PictureBar, TableBar, focusControl, listAnnouncement, pictureAnnouncement, tableAnnouncement, type TableAction, type TableFacts } from './RichTextObjectBar';
import { usePhoneWidth } from '../usePhoneWidth';
import { indentItem, isEmptyItem, itemAt, listFactsOf, listOf, listRefusal, LIST_START_MAX, LIST_START_MIN, outdentItem, previousListEnd, removeList, setListKind, setListStart, type ListAction, type ListFacts } from './richTextList';
import { drawCaptions, withoutDrawnCaptions } from './richTextCaption';
import { calloutAt, calloutRefused, coveredTopBlocks, labelCallouts, leaveCallout, lineAfterCallout, liftCallout, withoutCalloutLabels, wrapInCallout } from './richTextCallout';
import { alignPicture, captionPicture, minWidthPicture, pictureBeside, pictureFactsOf, removePicture, selectedPicture, sizePicture, type PictureAlignment, type PictureFacts } from './richTextPicture';
import { addColumn, addRow, cellAt, neighbourCell, removeColumn, removeRow, tableFactsOf, tableHtml, tableRefusal, tableSizeProblem, toggleHeaderRow } from './richTextTable';
import styles from './RichTextEditor.module.css';

/**
 * The Rich Text Editor (piece 5; figure 50): one toolbar over one editing
 * surface, shared by the Email Template composer, the Knowledge Base article
 * editor and the mail Compose screen. The shared core is headings, bold,
 * italic, underline and strikethrough, the two lists, image insertion and
 * block alignment, which every consumer keeps unless it opts out (decision 0770); the email additions are font family and size, text and
 * highlight colour, and Insert Placeholder for a merge field.
 *
 * 🔨 Every control applies the document's own editing command to the
 * selection, and takes the pointer without taking the focus, so a tap on a
 * phone formats what was selected. 💰 The surface is uncontrolled: the value
 * is written in only when it differs from what the surface holds, because
 * rendering it as content would put the caret back at the top on every
 * keystroke. 🔨 The surface keeps the browser's spell check and its own
 * correction menu; nothing here handles the context menu.
 *
 * The toolbar draws the icon set's glyphs and lights the commands the
 * selection already carries; a merge field is a tinted chip the surface
 * treats as one character (product owner, 2026-09-11: "make the email editor
 * better").
 *
 * 🔒 The email consumers draw the full set a mail program draws, in one row
 * whose right-hand groups fold into a More menu where the row is too narrow
 * (decision 1537; product owner, 2026-10-02: "make the compose more like the
 * industry standard"). A control the icon reference has no glyph for is drawn
 * as its words.
 */
export interface RichTextEditorProps {
  label: string;
  value: string;
  onChange: (html: string) => void;
  /** The email-only additions: font family and size, colours, and Insert Placeholder. */
  email?: boolean;
  placeholders?: readonly { key: string; label: string }[];
  placeholder?: string;
  name?: string;
  /**
   * 🔒 The text set (decisions 0704, 0770): the heading, the four marks, the two
   * lists and the four alignments, with no picture and no insert, for a consumer
   * such as the terms whose stored allowlist keeps no picture.
   */
  textOnly?: boolean;
  /**
   * 🔒 Align left, centre, right and justify (T272; decision 0770), offered on
   * every consumer unless it opts out here; the allowlist keeps the alignment on
   * the block it was set on and nothing else a style carries.
   */
  alignment?: boolean;
  /**
   * 🔒 The heading styles as a menu, Normal, Heading 1 and Heading 2, in place of
   * the one heading toggle (decision 0750): an article nests its headings, and
   * its table of contents reads both levels.
   */
  styleMenu?: boolean;
  /** 🔨 Whether the picture control is offered; the knowledge base withholds it until an article's picture can be a File (decision 0750). */
  pictures?: boolean;
  /** Markup a consumer inserts at the caret from a control of its own, handed what the surface holds now: the article's table of contents. */
  inserts?: readonly RichTextInsert[];
  /**
   * 🔒 The article's word-processor set (decision 0800): indent and outdent in a
   * list, the four sizes, text colour and highlight written as the bounded
   * styles its allowed table keeps, a link, a table and its five tools, and a
   * horizontal rule. 🔨 A consumer whose allowlist keeps none of it never draws
   * it; the email consumers keep their own set behind `email`.
   */
  wordProcessor?: boolean;
  /**
   * 🔒 A picture uploaded as a File (decision 0800), for a consumer whose words
   * store the File's id: handed the picked file, answered with its id and an
   * address to draw it from while the author writes. Absent, the picture control
   * inserts the bytes inline, as the email consumers take them.
   */
  onPicture?: ((picked: File) => Promise<RichTextPicture | { problem: string }>) | undefined;
  /** The picture types the upload accepts, where the consumer narrows them. */
  pictureTypes?: readonly string[] | undefined;
  /** The attribute an uploaded picture's File id is written under: the article's, or an email template's (decision 0965). */
  pictureAttribute?: 'data-kb-file' | 'data-mail-file';
  /** 🔒 The composed signature Insert signature places at the caret (decision 1537); the control is drawn only where a consumer hands one in. */
  signature?: string | null;
  /**
   * 🔒 The allowed table a stored value passes before it is written to the surface (T2956): the consumer's own, named as
   * `RichTextView` names it (decision 0800). Absent, the word-processor (article) editor takes the article table, the email editor the email table and every other editor the core one.
   */
  sanitize?: (html: string) => string;
  /**
   * 🔒 The allowed table a pasted document's markup passes before it reaches the surface (decision 7180): the notes keep a
   * pasted email's bold, colours, lists and links and nothing else. Absent, a paste is the browser's own.
   */
  sanitizePaste?: (html: string) => string;
  /** 🔒 The label stays for a screen reader and leaves the screen, where the field's place already says what it is: the composer's body under its Subject line (decision 1535). */
  labelHidden?: boolean;
}

/** A picture the consumer stored: the File's id the words keep, and the address the surface draws it from. */
export interface RichTextPicture {
  fileId: string;
  src: string;
}

/** One consumer's insert control (decision 0750): its glyph, its label, and the markup it places at the caret. */
export interface RichTextInsert {
  key: string;
  label: string;
  icon: IconMeaning;
  html: (current: string) => string;
}

/**
 * The blocks the style menu offers, as the document's own block command names
 * them: Normal, three headings, a quotation and a code block (decisions 0750,
 * 0800), and the three callouts after them, which the editor writes itself
 * (T3572; decision 10530).
 */
type CalloutBlock = 'NOTE' | 'WARNING' | 'TIP';
type StyleBlock = 'P' | 'H1' | 'H2' | 'H3' | 'BLOCKQUOTE' | 'PRE' | CalloutBlock;
const STYLE_BLOCKS: readonly { block: StyleBlock; key: 'rte.style.normal' | 'rte.style.heading1' | 'rte.style.heading2' | 'rte.style.heading3' | 'rte.style.quote' | 'rte.style.code' | 'kb.callout.note' | 'kb.callout.warning' | 'kb.callout.tip' }[] = [
  { block: 'P', key: 'rte.style.normal' },
  { block: 'H1', key: 'rte.style.heading1' },
  { block: 'H2', key: 'rte.style.heading2' },
  { block: 'H3', key: 'rte.style.heading3' },
  { block: 'BLOCKQUOTE', key: 'rte.style.quote' },
  { block: 'PRE', key: 'rte.style.code' },
  { block: 'NOTE', key: 'kb.callout.note' },
  { block: 'WARNING', key: 'kb.callout.warning' },
  { block: 'TIP', key: 'kb.callout.tip' },
];
const STYLE_BLOCK_OF: Readonly<Record<string, StyleBlock>> = { h1: 'H1', h2: 'H2', h3: 'H3', blockquote: 'BLOCKQUOTE', pre: 'PRE' };
/** 🔒 Each callout entry and the kind it writes (decision 10530). */
const CALLOUT_OF_BLOCK: Readonly<Partial<Record<StyleBlock, ArticleCalloutKind>>> = { NOTE: 'note', WARNING: 'warning', TIP: 'tip' };
const BLOCK_OF_CALLOUT: Readonly<Record<ArticleCalloutKind, CalloutBlock>> = { note: 'NOTE', warning: 'WARNING', tip: 'TIP' };

/**
 * 🔒 The size a run is drawn at (decision 1615), read off the nearest sized
 * element between the caret and the surface: a point size of the ladder, a
 * legacy `font` size by the point it is drawn as, the default where nothing is
 * sized, and null where the nearest size is one the ladder does not name, an
 * older article's `em` among them.
 */
export function pointSizeAt(surface: HTMLElement, from: Element | null): FontPointSize | null {
  for (let at = from; at && at !== surface && surface.contains(at); at = at.parentElement) {
    const declared = at instanceof HTMLElement ? at.style.fontSize : '';
    if (declared) return fontPointSizeOf(declared);
    if (at.tagName === 'FONT' && at.hasAttribute('size')) return LEGACY_FONT_SIZE_POINTS[at.getAttribute('size') as LegacyFontSize] ?? null;
  }
  return DEFAULT_FONT_POINT_SIZE;
}

/**
 * 🔨 Every legacy `font` size in the words rewritten as the point span it is drawn
 * as (decision 1615), before the Size menu marks a selection. The browser's own
 * size command is the mark, so a legacy size left standing would be read as one.
 */
export function legacySizesAsPoints(surface: HTMLElement): void {
  const fonts = Array.from(surface.querySelectorAll('font[size]'));
  if (fonts.length === 0) return;
  /*
   * 🔨 Moving a node out of the tree collapses a live range anchored in it, so
   * the selection's two ends are kept before the rewrite and put back after it:
   * a text node keeps its identity when it moves, and an end on a rewritten
   * font itself follows its words into the new span (review of ZC9).
   */
  const selection = document.getSelection();
  const kept = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const ends = kept ? { start: { node: kept.startContainer, offset: kept.startOffset }, end: { node: kept.endContainer, offset: kept.endOffset } } : null;
  const movedTo = new Map<Node, Node>();
  for (const font of fonts) {
    const points = LEGACY_FONT_SIZE_POINTS[font.getAttribute('size') as LegacyFontSize];
    font.removeAttribute('size');
    const span = points ? document.createElement('span') : null;
    if (span && points) {
      span.style.fontSize = fontPointValue(points);
      while (font.firstChild) span.appendChild(font.firstChild);
      font.appendChild(span);
      movedTo.set(font, span);
    }
    if (font.attributes.length === 0) font.replaceWith(...Array.from(font.childNodes));
  }
  if (!ends || !selection) return;
  const at = (end: { node: Node; offset: number }) => ({ node: movedTo.get(end.node) ?? end.node, offset: end.offset });
  const start = at(ends.start);
  const end = at(ends.end);
  if (!start.node.isConnected || !end.node.isConnected || !surface.contains(start.node) || !surface.contains(end.node)) return;
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  selection.removeAllRanges();
  selection.addRange(range);
}

/** A link the article keeps: the web, a mailbox or a telephone, and a bare address read as the web. */
const LINK_SCHEME = /^(?:https?:\/\/|mailto:|tel:)/i;
export function linkAddressOf(typed: string): string | null {
  const trimmed = typed.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  if (LINK_SCHEME.test(trimmed)) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return /^[^/]+\.[a-z]{2,}(?:[/?#].*)?$/i.test(trimmed) ? `https://${trimmed}` : null;
}

/** Where the caret stands, for the tools that act only there: in a link, and in a quotation the Quote toggle lifts (decision 1537). */
interface CaretPlace {
  link: boolean;
  quote: boolean;
  /** In a list item or an indented block, where Decrease indent has somewhere to go. */
  indented: boolean;
  /** Over a selection, or in words carrying a mark, a font, a colour, a spacing or a quotation, which Clear formatting can take off. */
  formatted: boolean;
  /** In an item of a numbered list, where the numbering menu has a list to act on (T2802). */
  numbered: boolean;
}
const NOWHERE: CaretPlace = { link: false, quote: false, indented: false, formatted: false, numbered: false };
const samePlace = (a: CaretPlace, b: CaretPlace): boolean => a.link === b.link && a.quote === b.quote && a.indented === b.indented && a.formatted === b.formatted && a.numbered === b.numbered;

/** What Clear formatting takes off, read where the caret stands (decision 1537). */
const FORMATTED = 'b, strong, i, em, u, s, strike, font, span[style], blockquote, [style*="line-height"]';

/** The blocks line spacing and clear formatting act on (decision 1537). */
const BLOCKS = 'p, div, li, blockquote, h1, h2, h3, h4, pre';

/** 🔒 A quotation lifted back into the words (decision 1537): its paragraphs stand where it stood, and words it held bare become one paragraph. */
function liftQuote(quote: HTMLElement): void {
  if (Array.from(quote.children).some((child) => child.matches(BLOCKS))) {
    quote.replaceWith(...Array.from(quote.childNodes));
    return;
  }
  const paragraph = document.createElement('p');
  paragraph.append(...Array.from(quote.childNodes));
  quote.replaceWith(paragraph);
}

/** The article's glyph buttons that are not one document command each (decision 0800); indent and outdent live in the list bar (decision 1270). */
type ArticleAction = 'link' | 'table' | 'horizontalRule';
/** The email toolbar's controls drawn as words, each pressed through one switch (decision 1537). */
type WordAction = 'undo' | 'redo' | 'quote' | 'clearFormatting' | 'signature';

/** What Undo and Redo can each act on. */
type HistoryState = { undo: boolean; redo: boolean };
/**
 * What the document's own history can do, where the browser answers, and null
 * where it does not (decision 1537). 🔨 Read rather than guessed: a person's
 * own Ctrl+Z undoes without pressing the button, and Redo must not stay greyed
 * then while Ctrl+Y would bring the words back.
 */
function documentHistory(): HistoryState | null {
  try {
    if (typeof document.queryCommandEnabled !== 'function') return null;
    return { undo: document.queryCommandEnabled('undo'), redo: document.queryCommandEnabled('redo') };
  } catch {
    // silent: a browser that does not report its edit commands leaves Undo and Redo as they stand.
    return null;
  }
}
/** An input the browser made by stepping its history, as Ctrl+Z and Ctrl+Y do. */
const isHistoryInput = (event: Event): boolean => 'inputType' in event && /^history(?:Undo|Redo)$/.test(String((event as InputEvent).inputType));

/**
 * 🔒 The one object the caret stands in, whose bar opens under the toolbar
 * (decisions 1240, 1243): the table and the cell the caret is in, or the
 * picture the selection holds whole.
 */
type HeldObject =
  | { kind: 'table'; table: HTMLTableElement; cell: HTMLTableCellElement }
  | { kind: 'picture'; picture: HTMLImageElement }
  | { kind: 'list'; list: HTMLOListElement | HTMLUListElement; item: HTMLLIElement };
type ObjectShown = { kind: 'table'; facts: TableFacts } | { kind: 'picture'; facts: PictureFacts } | { kind: 'list'; facts: ListFacts };

/**
 * 💰 The held object is marked on the surface for its outline, and the caret's
 * row and column for their tint (decision 1240). The marks are stripped from
 * everything the surface hands its consumer, so a stored body never carries one
 * and the surface is never rewritten over its own marks.
 */
const MARKS = / data-rte-(?:object|tint)="[^"]*"/g;
export function surfaceHtml(node: HTMLElement): string {
  // 🔒 The captions drawn under the pictures are the editor's alone (T1260; decision 1943): the picture's attribute is the caption, so the drawn line never reaches a stored body.
  // 🔒 A callout's label is drawn for a screen reader and never stored either (decision 10530).
  return withoutCalloutLabels(withoutDrawnCaptions(node.innerHTML.replace(MARKS, '')));
}
function clearMarks(node: HTMLElement): void {
  for (const marked of Array.from(node.querySelectorAll('[data-rte-object], [data-rte-tint]'))) {
    marked.removeAttribute('data-rte-object');
    marked.removeAttribute('data-rte-tint');
  }
}
function markTable(node: HTMLElement, table: HTMLTableElement, cell: HTMLTableCellElement): void {
  clearMarks(node);
  table.setAttribute('data-rte-object', 'table');
  const row = cell.parentElement as HTMLTableRowElement | null;
  for (const each of Array.from(table.rows)) {
    for (const other of Array.from(each.cells)) {
      if (each === row) other.setAttribute('data-rte-tint', 'row');
      else if (other.cellIndex === cell.cellIndex) other.setAttribute('data-rte-tint', 'column');
    }
  }
}
function markPicture(node: HTMLElement, picture: HTMLImageElement): void {
  clearMarks(node);
  picture.setAttribute('data-rte-object', 'picture');
}
/** The list the caret's item stands in is outlined, the same mark the table and the picture take (decision 1270). */
function markList(node: HTMLElement, list: HTMLElement): void {
  clearMarks(node);
  list.setAttribute('data-rte-object', 'list');
}
function sameFacts(a: ObjectShown | null, b: ObjectShown | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'picture' || b.kind === 'picture') {
    if (a.kind !== 'picture' || b.kind !== 'picture') return false;
    return a.facts.width === b.facts.width && a.facts.minWidth === b.facts.minWidth && a.facts.align === b.facts.align && a.facts.alt === b.facts.alt && a.facts.caption === b.facts.caption;
  }
  if (a.kind === 'list' || b.kind === 'list') {
    if (a.kind !== 'list' || b.kind !== 'list') return false;
    const [x, y] = [a.facts, b.facts];
    return x.kind === y.kind && x.level === y.level && x.items === y.items && x.position === y.position && x.start === y.start && x.deepest === y.deepest && x.parent?.kind === y.parent?.kind && x.parent?.step === y.parent?.step;
  }
  return a.facts.rows === b.facts.rows && a.facts.columns === b.facts.columns && a.facts.header === b.facts.header && a.facts.inHeader === b.facts.inHeader;
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const FONTS = ['Arial', 'Georgia', 'Helvetica', 'Times New Roman', 'Verdana'] as const;

/** The commands whose state the toolbar reflects, read off the selection with the document's own query. */
const STATEFUL = ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList', 'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'] as const;

export const richTextEditorDocumentedStates = ['core', 'email'] as const;

/** The chip a merge field is inserted as: one uneditable span the resolver still reads as {{key}}. */
export function mergeFieldHtml(key: string): string {
  const safe = key.replace(/[^A-Za-z0-9_.-]/g, '');
  return `<span data-merge="${safe}" contenteditable="false">{{${safe}}}</span>&nbsp;`;
}

export function RichTextEditor({ label, value, onChange, email = false, placeholders = [], placeholder, name, textOnly = false, alignment = true, styleMenu = false, pictures = true, inserts = [], wordProcessor = false, onPicture, pictureTypes, pictureAttribute = 'data-kb-file', signature = null, labelHidden = false, sanitize, sanitizePaste }: RichTextEditorProps) {
  const surface = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const saved = useRef<Range | null>(null);
  const id = useId();
  const [active, setActive] = useState<ReadonlySet<string>>(() => new Set());
  const [heading, setHeading] = useState(false);
  const [block, setBlock] = useState<StyleBlock>('P');
  /** The point size at the caret, which the Size menu draws selected; null draws it blank (decision 1615). */
  const [pointSize, setPointSize] = useState<FontPointSize | null>(DEFAULT_FONT_POINT_SIZE);
  /** A size chosen at a caret, written onto the words typed next (decision 1615). */
  const pendingSize = useRef<FontPointSize | null>(null);
  /** Where the caret stood when that size was chosen; a caret that moves away, or a blur, drops the size (T866). */
  const pendingAt = useRef<{ node: Node | null; offset: number } | null>(null);
  const [place, setPlace] = useState<CaretPlace>(NOWHERE);
  const [panel, setPanel] = useState<'link' | 'table' | 'listStart' | null>(null);
  const [linkTyped, setLinkTyped] = useState('');
  const [linkProblem, setLinkProblem] = useState<string | null>(null);
  const [listStart, setListStartTyped] = useState<number | null>(2);
  const [numberingNotice, setNumberingNotice] = useState<string | null>(null);
  const [rows, setRows] = useState<number | null>(3);
  const [columns, setColumns] = useState<number | null>(3);
  const [headerRow, setHeaderRow] = useState(true);
  const [pictureNotice, setPictureNotice] = useState<string | null>(null);
  /** 🔒 Why a callout was refused, beside the words, until the next style is picked (decision 10530). */
  const [calloutNotice, setCalloutNotice] = useState<string | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const held = useRef<HeldObject | null>(null);
  const announced = useRef<Element | null>(null);
  const [shown, setShown] = useState<ObjectShown | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [asking, setAsking] = useState(false);
  const askFromKeyboard = useRef(false);
  const escaped = useRef(false);
  /** Where the caret last stood in the words, which a list press puts it back to when the focus is on the bar. */
  const lastCaret = useRef<{ node: Node; offset: number } | null>(null);
  const phone = usePhoneWidth();
  /**
   * 🔒 The email toolbar's fold (decision 1537): how many groups from the right
   * stand in the More menu, from each group's width as last drawn in the row.
   * The row is measured after every render and whenever it is resized.
   */
  const toolbar = useRef<HTMLDivElement>(null);
  const overflow = useRef<HTMLDivElement>(null);
  /** Each group's width as last drawn in the row, by its name, so a group joining later (a signature arriving) leaves the others' widths standing. */
  const groupWidths = useRef(new Map<string, number>());
  const moreWidth = useRef(0);
  /** The groups the toolbar draws, in order, for the resize observer's measure. */
  const groupKeys = useRef<readonly string[]>([]);
  const [folded, setFolded] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  /** How many groups from the right the row has no room for, from the widths last drawn; it reads refs alone, so it is made once. */
  const measureFold = useCallback(() => {
    const row = toolbar.current;
    if (!row) return;
    for (const group of Array.from(row.querySelectorAll<HTMLElement>('[data-rich-text-group-index]'))) groupWidths.current.set(group.dataset.richTextGroup ?? '', group.offsetWidth);
    const more = row.querySelector<HTMLElement>('[data-rich-text-more]');
    if (more) moreWidth.current = more.offsetWidth;
    else if (moreWidth.current === 0) moreWidth.current = row.querySelector<HTMLElement>('[data-rich-text-tool]')?.offsetWidth ?? 0;
    const style = window.getComputedStyle(row);
    const available = row.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    // A group not yet drawn in the row is taken as twice More's width until it is.
    const widths = groupKeys.current.map((key) => groupWidths.current.get(key) ?? moreWidth.current * 2);
    const next = foldedGroups(widths, available, moreWidth.current, parseFloat(style.columnGap) || 0);
    setFolded((was) => (was === next ? was : next));
  }, []);
  /** Whether Undo and Redo have anything to act on, so neither is pressed to no effect (decisions 0559, 1537). */
  const [history, setHistory] = useState<HistoryState>({ undo: false, redo: false });
  /** The words as last handed up or written in, which a change is measured against. */
  const emitted = useRef<string | null>(null);

  useEffect(() => {
    const node = surface.current;
    // 🔒 A value the surface did not hand up itself passes the allowed table first (T2956): an event handler or a `javascript:` link in a stored value never reaches the DOM, whatever the page's script policy does.
    if (node && surfaceHtml(node) !== value) {
      const safe = (sanitize ?? (wordProcessor ? sanitizeArticleSurface : email ? sanitizeEmailSurface : sanitizeCoreSurface))(value);
      if (surfaceHtml(node) !== safe) node.innerHTML = safe;
    }
    if (node) drawCaptions(node);
    if (node && wordProcessor) labelCallouts(node);
    if (node) emitted.current = surfaceHtml(node);
  }, [value, sanitize, email, wordProcessor]);

  /**
   * 🔒 The object the caret stands in (decision 1240), read with the lit state:
   * a picture selected whole, else the innermost of the list item or the table
   * cell the caret is in (decision 1270), and nothing anywhere else. The bar is
   * the article's alone, so no other consumer tracks one.
   */
  const trackObject = useCallback((node: HTMLElement, anchor: Node | null) => {
    const picture = selectedPicture(node, document.getSelection());
    if (picture) {
      held.current = { kind: 'picture', picture };
      markPicture(node, picture);
      const next: ObjectShown = { kind: 'picture', facts: pictureFactsOf(node, picture) };
      setShown((was) => (sameFacts(was, next) ? was : next));
      setAsking(false);
      if (announced.current !== picture) {
        announced.current = picture;
        setAnnouncement(pictureAnnouncement(next.facts));
      }
      return;
    }
    const cell = cellAt(node, anchor);
    const item = itemAt(node, anchor);
    // A list inside a cell, or a table inside a list item: the one nearer the caret is the one held.
    if (item && (!cell || cell.contains(item))) {
      const list = listOf(item);
      held.current = { kind: 'list', list, item };
      markList(node, list);
      const next: ObjectShown = { kind: 'list', facts: listFactsOf(node, item) };
      setShown((was) => (sameFacts(was, next) ? was : next));
      setAsking(false);
      if (announced.current !== list) {
        announced.current = list;
        setAnnouncement(listAnnouncement(next.facts));
      }
      return;
    }
    const table = cell?.closest('table') ?? null;
    if (!cell || !table || !node.contains(table)) {
      if (held.current) clearMarks(node);
      held.current = null;
      announced.current = null;
      setShown(null);
      setAsking(false);
      return;
    }
    held.current = { kind: 'table', table, cell };
    markTable(node, table, cell);
    const next: ObjectShown = { kind: 'table', facts: tableFactsOf(table, cell) };
    setShown((was) => (sameFacts(was, next) ? was : next));
    // The object is announced once, when the caret first enters it, never on every move within it.
    if (announced.current !== table) {
      announced.current = table;
      setAsking(false);
      setAnnouncement(tableAnnouncement(next.facts));
    }
  }, []);

  /**
   * What the selection already carries, read off the document. 🔨 It is read
   * again the moment a command has run, not only when the selection changes
   * (decision 0559): pressing Bold with the caret on an empty line toggles the
   * pending state and writes no markup, so nothing on the screen moved and the
   * person could not tell the press had landed.
   */
  const readCommandState = useCallback(() => {
    const node = surface.current;
    const selection = document.getSelection();
    if (!node || !selection || selection.rangeCount === 0 || !node.contains(selection.anchorNode)) return;
    if (selection.anchorNode) lastCaret.current = { node: selection.anchorNode, offset: selection.anchorOffset };
    const next = new Set<string>();
    for (const command of STATEFUL) {
      try {
        if (document.queryCommandState(command)) next.add(command);
      } catch {
        // silent: a command the browser does not report is simply unlit
        // A command the browser does not report is simply unlit.
      }
    }
    setActive((was) => (was.size === next.size && [...was].every((c) => next.has(c)) ? was : next));
    try {
      const current = String(document.queryCommandValue('formatBlock')).toLowerCase();
      setHeading(/^h[1-6]$/.test(current));
      setBlock(STYLE_BLOCK_OF[current] ?? 'P');
    } catch {
      // silent: a selection the browser cannot read draws as a paragraph.
      setHeading(false);
      setBlock('P');
    }
    // Where the caret stands, for the link tool that acts only there (decision 0800).
    const anchor = selection.anchorNode;
    const element = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
    const inside = (selector: string) => {
      const found = element?.closest(selector);
      return Boolean(found && node.contains(found));
    };
    // 🔒 The caret in a callout reads the callout's kind in the style menu (decision 10530).
    const callout = wordProcessor ? calloutAt(node, anchor) : null;
    if (callout) setBlock(BLOCK_OF_CALLOUT[callout.kind]);
    const sized = pointSizeAt(node, element);
    setPointSize((was) => (was === sized ? was : sized));
    const where: CaretPlace = { link: inside('a'), quote: inside('blockquote'), indented: inside('li, blockquote'), formatted: !selection.isCollapsed || inside(FORMATTED), numbered: element?.closest('li')?.parentElement?.tagName === 'OL' && node.contains(element.closest('li')) };
    setPlace((was) => (samePlace(was, where) ? was : where));
    if (wordProcessor) trackObject(node, anchor);
  }, [wordProcessor, trackObject]);

  // The lit buttons follow the selection: read once per selection change while the caret is inside this surface.
  useEffect(() => {
    const onChange = () => {
      const was = pendingAt.current;
      const selection = document.getSelection();
      if (pendingSize.current !== null && was && selection && selection.rangeCount > 0 && (selection.anchorNode !== was.node || selection.anchorOffset !== was.offset)) {
        pendingSize.current = null;
        pendingAt.current = null;
      }
      readCommandState();
    };
    document.addEventListener('selectionchange', onChange);
    return () => document.removeEventListener('selectionchange', onChange);
  }, [readCommandState]);

  /** `step` says the change was a step through the history itself, Undo's or Redo's, by button or by key. */
  const emit = (step: 'edit' | 'history' = 'edit') => {
    if (!surface.current) return;
    // 🔒 Every change brings the drawn captions into line with the pictures' own, so a caption typed in the bar shows under its picture at once and a picture taken out takes its caption with it (T1260; decision 1943).
    drawCaptions(surface.current);
    if (wordProcessor) labelCallouts(surface.current);
    const html = surfaceHtml(surface.current);
    const known = documentHistory();
    if (known) setHistory((was) => (was.undo === known.undo && was.redo === known.redo ? was : known));
    // 🔒 Where the browser does not say: a step through the history leaves both open, a change since the editor opened is what Undo has to take back, and any other change ends what Redo could bring back (decision 1537).
    else if (step === 'history') setHistory((was) => (was.undo && was.redo ? was : { undo: true, redo: true }));
    else if (emitted.current !== null && html !== emitted.current) setHistory((was) => (was.undo && !was.redo ? was : { undo: true, redo: false }));
    emitted.current = html;
    onChange(html);
  };
  /**
   * 🔨 The caret brought back into the words where the selection stands outside
   * them, at the place it last stood or else at the end. Focusing a surface that
   * already holds the focus moves no selection, and Align left on an empty body
   * leaves the browser's selection in a toolbar button's text, so every command
   * after it acted on nothing (found on landing run 1404, 2026-10-02).
   */
  const caretInWords = (node: HTMLElement) => {
    const selection = document.getSelection();
    if (!selection) return;
    if (selection.rangeCount > 0 && selection.anchorNode && node.contains(selection.anchorNode)) {
      lastCaret.current = { node: selection.anchorNode, offset: selection.anchorOffset };
      return;
    }
    const at = lastCaret.current;
    const range = document.createRange();
    if (at && at.node.isConnected && node.contains(at.node)) {
      range.setStart(at.node, Math.min(at.offset, at.node.nodeType === Node.TEXT_NODE ? (at.node.textContent ?? '').length : at.node.childNodes.length));
      range.collapse(true);
    } else {
      range.selectNodeContents(node);
      range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  };
  /** The command runs against the selection; the pointer never took the focus, so the selection is still there. */
  const run = (command: string, argument?: string) => {
    const node = surface.current;
    node?.focus();
    // 🔒 Every command acts in the words, and leaves the caret in them (decision 1537).
    if (node) caretInWords(node);
    const caretOnly = document.getSelection()?.isCollapsed ?? true;
    document.execCommand(command, false, argument);
    if (node) caretInWords(node);
    emit();
    /**
     * 🔨 Only a caret re-reads here (decision 0559). A command run over a real
     * selection rewrites the markup, and the browser's own selectionchange
     * lights the toolbar from that, as it always did. A caret writes nothing,
     * so nothing would tell the toolbar the press had landed.
     *
     * 💰 Reading on every command put a state write in the same tick as the
     * value going up to the parent and coming back as a fresh `innerHTML`, so
     * the surface was rewritten while a test was still deciding whether it had
     * stopped moving; the acceptance suite's formatting case timed out clicking
     * it, twice, in the container and never here (found on CI, 2026-09-18).
     */
    if (caretOnly) readCommandState();
  };
  const keepFocus = (event: MouseEvent) => event.preventDefault();

  /** The selection a panel or an upload takes the focus from, kept so the tool acts where the author was. */
  const saveSelection = () => {
    const selection = document.getSelection();
    const node = surface.current;
    saved.current = selection && selection.rangeCount > 0 && node && node.contains(selection.anchorNode) ? selection.getRangeAt(0).cloneRange() : null;
  };
  const restoreSelection = () => {
    surface.current?.focus();
    const selection = document.getSelection();
    if (saved.current && selection) {
      selection.removeAllRanges();
      selection.addRange(saved.current);
    }
  };

  /**
   * 🔒 A colour or a highlight is written as the one declaration the article
   * keeps (decision 0800): the command runs with styles on, then off again, so
   * bold and the lists still write their own elements.
   */
  const runStyled = (command: string, argument: string) => {
    surface.current?.focus();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, argument);
    document.execCommand('styleWithCSS', false, 'false');
    emit();
  };

  /**
   * 🔒 Each mark the browser's size command left becomes a span carrying the
   * size in points, or nothing at all for the default (decision 1615); a size
   * already inside the mark gives way to the new one. The marks are the browser's
   * seventh legacy size, which no stored body carries once its legacy sizes are
   * points.
   */
  const settleSizeMarks = (node: HTMLElement, size: FontPointSize): Node[] => {
    const placed: Node[] = [];
    const sizedAround = (mark: Element): HTMLElement | null => {
      for (let at = mark.parentElement; at && at !== node && node.contains(at); at = at.parentElement) if (at.style.fontSize) return at;
      return null;
    };
    for (const mark of Array.from(node.querySelectorAll('font[size="7"]'))) {
      for (const inner of Array.from(mark.querySelectorAll<HTMLElement>('[style]'))) {
        inner.style.removeProperty('font-size');
        if (!inner.getAttribute('style')) inner.removeAttribute('style');
      }
      // A size around the mark that holds nothing but the mark's words gives way to the new one, so a run re-sized leaves no size behind it.
      let around = sizedAround(mark);
      while (around && around.textContent === mark.textContent) {
        around.style.removeProperty('font-size');
        if (!around.getAttribute('style')) around.removeAttribute('style');
        if (around.tagName === 'SPAN' && around.attributes.length === 0) around.replaceWith(...Array.from(around.childNodes));
        around = sizedAround(mark);
      }
      // 🔒 The default writes nothing, unless a size around part of a run would still draw; then it is written as itself.
      if (size === DEFAULT_FONT_POINT_SIZE && !around) {
        const children = Array.from(mark.childNodes);
        mark.replaceWith(...children);
        placed.push(...children);
      } else {
        const span = document.createElement('span');
        span.style.fontSize = fontPointValue(size);
        while (mark.firstChild) span.appendChild(mark.firstChild);
        mark.replaceWith(span);
        placed.push(span);
      }
    }
    return placed;
  };

  /**
   * 🔒 A size is one of the point ladder (decision 1615), the same in the email
   * and the article editor. The document's own size command marks the selection
   * and each mark becomes a point span. At a caret nothing is marked yet, so the
   * size waits for the words typed next.
   */
  const applySize = (size: FontPointSize) => {
    const node = surface.current;
    if (!node) return;
    // The menu took the focus; the selection the person reached for it with comes back first.
    restoreSelection();
    caretInWords(node);
    legacySizesAsPoints(node);
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand('fontSize', false, '7');
    const placed = settleSizeMarks(node, size);
    const first = placed[0];
    const last = placed[placed.length - 1];
    const selection = document.getSelection();
    pendingSize.current = first ? null : size;
    pendingAt.current = first || !selection ? null : { node: selection.anchorNode, offset: selection.anchorOffset };
    if (!first) setPointSize(size);
    if (first && last && selection) {
      const range = document.createRange();
      range.setStartBefore(first);
      range.setEndAfter(last);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    emit();
  };

  /** 🔒 The words typed after a size was chosen at a caret take that size (decision 1615), the caret staying where the typing left it. */
  const settlePendingSize = () => {
    const node = surface.current;
    const size = pendingSize.current;
    if (!node || size === null || !node.querySelector('font[size="7"]')) return;
    const selection = document.getSelection();
    const at = selection && selection.rangeCount > 0 ? { node: selection.anchorNode, offset: selection.anchorOffset } : null;
    settleSizeMarks(node, size);
    pendingSize.current = null;
    pendingAt.current = null;
    if (at?.node && at.node.isConnected && node.contains(at.node) && selection) {
      const range = document.createRange();
      range.setStart(at.node, at.offset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  /**
   * The caret put back in a cell after a bar press (decision 1240), and kept as
   * the place Esc returns to. 🔨 A press made from the keyboard keeps the focus
   * on the control that made it, so the next press follows without a mouse.
   */
  const settleIn = (target: Node | null, atStart: boolean | 'whole' = true) => {
    const node = surface.current;
    const selection = document.getSelection();
    if (!node || !selection) return;
    const pressed = document.activeElement instanceof HTMLElement && bar.current?.contains(document.activeElement) ? document.activeElement : null;
    const range = document.createRange();
    if (target && node.contains(target) && atStart === 'whole') {
      range.selectNode(target);
    } else if (target && node.contains(target)) {
      range.selectNodeContents(target);
      range.collapse(atStart === true);
    } else {
      range.selectNodeContents(node);
      range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
    saved.current = range.cloneRange();
    readCommandState();
    if (pressed && pressed.isConnected) pressed.focus();
    else if (pressed) node.focus();
  };

  /**
   * 🔒 A table bar press acts on the table the caret stands in and on nothing
   * else (decisions 1240, 1241). A press the bounds refuse does nothing, as its
   * greyed button already says; Delete Table only opens its question.
   */
  const onTable = (action: TableAction) => {
    const node = surface.current;
    const object = held.current;
    if (!node || object?.kind !== 'table' || !node.contains(object.cell)) return;
    const { table, cell } = object;
    if (action !== 'header' && action !== 'delete' && tableRefusal(action, tableFactsOf(table, cell)) !== null) return;
    if (action === 'delete') {
      saveSelection();
      askFromKeyboard.current = document.activeElement instanceof HTMLElement && (bar.current?.contains(document.activeElement) ?? false);
      setAsking(true);
      return;
    }
    const row = cell.parentElement as HTMLTableRowElement;
    const rows = Array.from(table.rows);
    const at = rows.indexOf(row);
    const after = table.nextSibling;
    let next: Node | null = cell;
    switch (action) {
      case 'rowAbove':
        addRow(cell, 'above');
        break;
      case 'rowBelow':
        addRow(cell, 'below');
        break;
      case 'rowRemove': {
        const neighbour = rows[at + 1] ?? rows[at - 1];
        next = neighbour ? neighbour.cells[Math.min(cell.cellIndex, neighbour.cells.length - 1)] ?? null : after;
        removeRow(cell);
        break;
      }
      case 'columnLeft':
        addColumn(cell, 'left');
        break;
      case 'columnRight':
        addColumn(cell, 'right');
        break;
      case 'columnRemove':
        next = row.cells[cell.cellIndex + 1] ?? row.cells[cell.cellIndex - 1] ?? after;
        removeColumn(cell);
        break;
      case 'header': {
        const index = cell.cellIndex;
        toggleHeaderRow(cell);
        // The header row's cells are made anew, so the caret goes to the cell standing where it stood.
        next = table.rows[at]?.cells[index] ?? null;
        break;
      }
    }
    emit();
    settleIn(next);
  };

  /** 🔒 Delete Table, once asked (decision 1241): the table and its words go, and the caret stands in the paragraph that followed it. */
  const confirmDelete = () => {
    const node = surface.current;
    const object = held.current;
    if (!node || object?.kind !== 'table' || !node.contains(object.table)) return;
    let after = object.table.nextElementSibling;
    if (!after) {
      after = document.createElement('p');
      after.innerHTML = '<br>';
      object.table.after(after);
    }
    object.table.remove();
    setAsking(false);
    emit();
    settleIn(after);
  };
  /** Keep It, or Esc: the question closes, and the caret is where it was. */
  const keepTable = () => {
    setAsking(false);
    restoreSelection();
  };
  // 🔨 A question opened from the keyboard takes the focus onto Keep It, so a second Enter keeps the table rather than deleting it.
  useEffect(() => {
    if (!asking || !askFromKeyboard.current) return;
    askFromKeyboard.current = false;
    bar.current?.querySelector<HTMLElement>('[data-bar-control="tableDeleteKeep"]')?.focus();
  }, [asking]);

  /** The picture the bar acts on, while it is still in the words. */
  const heldPicture = (): HTMLImageElement | null => {
    const object = held.current;
    return object?.kind === 'picture' && surface.current?.contains(object.picture) ? object.picture : null;
  };
  /** The bar redrawn from the picture as it now stands, the selection left where it is so a field being typed in keeps its caret. */
  const showPicture = (picture: HTMLImageElement) => {
    const node = surface.current;
    if (!node) return;
    const next: ObjectShown = { kind: 'picture', facts: pictureFactsOf(node, picture) };
    setShown((was) => (sameFacts(was, next) ? was : next));
  };
  /** 🔒 A picture selected whole opens its bar (decision 1243): a click, or Alt+F10 from beside it. */
  const selectPicture = (picture: HTMLImageElement) => settleIn(picture, 'whole');
  /** 🔒 A width the article keeps, 10 to 100 percent of the column (decision 1242). */
  const onPictureSize = (percent: number) => {
    const picture = heldPicture();
    if (!picture) return;
    sizePicture(picture, percent);
    emit();
    showPicture(picture);
  };
  /** 🔒 The minimum a picture keeps on screen, 100 to 1200 pixels, or none for the 200 default (decision 1942). */
  const onPictureMinWidth = (pixels: number | null) => {
    const picture = heldPicture();
    if (!picture) return;
    minWidthPicture(picture, pixels);
    emit();
    showPicture(picture);
  };
  /** 🔒 The picture's own line takes the alignment, and text never wraps beside it (decision 1243). */
  const onPictureAlign = (align: PictureAlignment) => {
    const node = surface.current;
    const picture = heldPicture();
    if (!node || !picture) return;
    alignPicture(node, picture, align);
    emit();
    selectPicture(picture);
  };
  const onPictureAlt = (alt: string) => {
    const picture = heldPicture();
    if (!picture) return;
    picture.setAttribute('alt', alt);
    emit();
    showPicture(picture);
  };
  /** 🔒 The caption the reader and the PDF draw under the picture (T724; decision 1476), kept apart from its alt text. */
  const onPictureCaption = (caption: string) => {
    const picture = heldPicture();
    if (!picture) return;
    captionPicture(picture, caption);
    emit();
    showPicture(picture);
  };
  /** 🔒 Remove Picture takes it out of the words, and the caret stands where it was (decision 1243). */
  const onPictureRemove = () => {
    const picture = heldPicture();
    if (!picture) return;
    const line = removePicture(picture);
    emit();
    settleIn(line);
  };
  /**
   * The caret put back where it stood after a list press moved its item
   * (decision 1270): the same word and letter, since the words moved whole, or
   * the start of the line the item became where the caret stood on the item
   * itself. A press from the bar keeps the focus on the control, as settleIn does.
   */
  const keepCaret = (change: () => Node | null) => {
    const node = surface.current;
    const selection = document.getSelection();
    // The live caret where it is still in the words; the last one read there where the focus has gone to the bar.
    const at = node && selection && selection.rangeCount > 0 && selection.anchorNode && node.contains(selection.anchorNode) ? { node: selection.anchorNode, offset: selection.anchorOffset } : lastCaret.current;
    const fallback = change();
    emit();
    if (node && selection && at?.node && at.node.isConnected && node.contains(at.node)) {
      const pressed = document.activeElement instanceof HTMLElement && bar.current?.contains(document.activeElement) ? document.activeElement : null;
      const range = document.createRange();
      range.setStart(at.node, Math.min(at.offset, at.node.nodeType === Node.TEXT_NODE ? (at.node.textContent ?? '').length : at.node.childNodes.length));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      saved.current = range.cloneRange();
      readCommandState();
      if (pressed && pressed.isConnected) pressed.focus();
      return;
    }
    settleIn(fallback);
  };
  /** The list item the bar acts on, while it is still in the words. */
  const heldItem = (): HTMLLIElement | null => {
    const object = held.current;
    return object?.kind === 'list' && surface.current?.contains(object.item) ? object.item : null;
  };
  /**
   * 🔒 A list bar press (decisions 1270, 1271): the kind for the whole list at
   * the caret's level, Indent and Outdent for the caret's item, Remove List for
   * the whole list. A press the bar greys does nothing, as its button says.
   */
  const onList = (action: ListAction) => {
    const node = surface.current;
    const item = heldItem();
    if (!node || !item) return;
    switch (action) {
      case 'bulleted':
      case 'numbered':
        return keepCaret(() => {
          const next = setListKind(listOf(item), action);
          // The same list under another kind is not a new object to announce.
          announced.current = next;
          return item;
        });
      case 'indent':
        if (listRefusal(listFactsOf(node, item)) !== null) return;
        return keepCaret(() => {
          indentItem(node, item);
          return item;
        });
      case 'outdent':
        return keepCaret(() => outdentItem(node, item));
      case 'remove':
        return keepCaret(() => removeList(node, item));
    }
  };
  /** 🔒 Start At on a numbered list (decision 1270): the bar redrawn from the list as it now stands, the box keeping its caret. */
  const onListStart = (start: number) => {
    const node = surface.current;
    const item = heldItem();
    if (!node || !item) return;
    setListStart(listOf(item), start);
    emit();
    const next: ObjectShown = { kind: 'list', facts: listFactsOf(node, item) };
    setShown((was) => (sameFacts(was, next) ? was : next));
  };
  /** 🔨 A click on a picture selects it; a tap does too, and never opens a phone's keyboard, so only a fine pointer takes the focus. */
  const onSurfaceMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!wordProcessor || !(event.target instanceof HTMLImageElement)) return;
    event.preventDefault();
    const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (!coarse) surface.current?.focus();
    selectPicture(event.target);
  };
  // Alt+F10 from beside a picture selects it first; the bar it opens then takes the focus.
  const focusBarNext = useRef(false);
  useEffect(() => {
    if (!focusBarNext.current || !shown) return;
    focusBarNext.current = false;
    focusControl(bar.current?.querySelector<HTMLElement>('[data-bar-control]'));
  }, [shown]);

  /**
   * 🔒 The keys the held object answers (decisions 1240, 1241, 1272): Alt+F10
   * moves the focus onto its bar, and does nothing where no object is held; Tab
   * and Shift+Tab walk a table's cells and leave it past either end, never
   * adding a row; in a list item they indent and outdent it, and Enter on an
   * empty item outdents it; Esc then Tab leaves the editor, so Tab is never a
   * trap.
   */
  /** 🔒 A paste through the consumer's paste table (decision 7180); words with no markup paste as the browser pastes them. */
  const onSurfacePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!sanitizePaste) return;
    const html = event.clipboardData?.getData('text/html') ?? '';
    if (html.trim() === '') return;
    event.preventDefault();
    document.execCommand('insertHTML', false, sanitizePaste(html));
    emit();
  };
  const onSurfaceKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!wordProcessor) return;
    const node = surface.current;
    const wasEscaped = escaped.current;
    escaped.current = event.key === 'Escape';
    if (event.key === 'Escape' && asking) {
      event.preventDefault();
      keepTable();
      return;
    }
    if (event.key === 'F10' && event.altKey) {
      const beside = held.current || !node ? null : pictureBeside(node, document.getSelection());
      if (held.current) {
        event.preventDefault();
        saveSelection();
        focusControl(bar.current?.querySelector<HTMLElement>('[data-bar-control]'));
      } else if (beside) {
        event.preventDefault();
        focusBarNext.current = true;
        selectPicture(beside);
      }
      return;
    }
    const listItem = node && held.current?.kind === 'list' && node.contains(held.current.item) ? held.current.item : null;
    if (event.key === 'Enter' && listItem && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey && isEmptyItem(listItem)) {
      // 🔒 Enter on an empty item leaves the list a level at a time, as Outdent does (decision 1272).
      event.preventDefault();
      onList('outdent');
      return;
    }
    if (event.key === 'Enter' && node && !listItem && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      // 🔒 Enter on an empty last line leaves the callout, the line moving out after it (decision 10530).
      const line = leaveCallout(node, document.getSelection()?.anchorNode ?? null);
      if (line) {
        event.preventDefault();
        emit();
        settleIn(line);
        return;
      }
    }
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey || wasEscaped || !node) return;
    if (listItem) {
      // 🔒 Tab indents the item and Shift+Tab outdents it (decision 1272); an indent the bar greys stays put and says why, heard once.
      event.preventDefault();
      if (event.shiftKey) return onList('outdent');
      const refusal = listRefusal(listFactsOf(node, listItem));
      if (refusal === null) return onList('indent');
      setAnnouncement(t(refusal === 'firstItem' ? 'rte.list.firstItem' : 'rte.list.maxDepth'));
      return;
    }
    const cell = cellAt(node, document.getSelection()?.anchorNode ?? null);
    if (!cell) return;
    const next = neighbourCell(cell, event.shiftKey);
    if (next) {
      event.preventDefault();
      settleIn(next, !event.shiftKey);
      return;
    }
    const table = cell.closest('table');
    const beyond = event.shiftKey ? table?.previousElementSibling : table?.nextElementSibling;
    // Past either end the caret leaves the table for the text beside it; with no text there, Tab leaves the editor.
    if (beyond && node.contains(beyond)) {
      event.preventDefault();
      settleIn(beyond, !event.shiftKey);
    }
  };

  /** The numbered list the caret stands in, while it is in the words (T2802). */
  const numberedAtCaret = (): HTMLOListElement | null => {
    const node = surface.current;
    const anchor = document.getSelection()?.anchorNode ?? null;
    const item = node ? itemAt(node, anchor) : null;
    const list = item ? listOf(item) : null;
    return list && list.tagName === 'OL' ? (list as HTMLOListElement) : null;
  };
  /** 🔒 The Numbers tool's menu (T2802; Start At takes 1 to 999, and 1 clears the list's start, decision 6462): Continue Previous List opens the list one past the numbered list before it, Start At opens a number box. */
  const pressNumbering = (choice: string) => {
    setNumberingNotice(null);
    saveSelection();
    if (choice === 'startAt') {
      const list = numberedAtCaret();
      setListStartTyped(list ? Math.max(Number(list.getAttribute('start')) || LIST_START_MIN, LIST_START_MIN) : LIST_START_MIN);
      return setPanel('listStart');
    }
    restoreSelection();
    const list = numberedAtCaret();
    if (!list) return;
    const end = previousListEnd(list);
    if (end === null) return setNumberingNotice(t('rte.numbering.noPrevious'));
    setListStart(list, Math.min(end + 1, LIST_START_MAX));
    emit();
  };
  const applyListStart = () => {
    if (listStart === null || listStart < LIST_START_MIN || listStart > LIST_START_MAX) return;
    restoreSelection();
    const list = numberedAtCaret();
    if (list) setListStart(list, listStart);
    setPanel(null);
    emit();
  };
  const openPanel = (which: 'link' | 'table') => {
    saveSelection();
    setLinkTyped('');
    setLinkProblem(null);
    setPanel(which);
  };
  const applyLink = () => {
    const address = linkAddressOf(linkTyped);
    if (!address) return setLinkProblem(t('rte.link.problem'));
    restoreSelection();
    if (document.getSelection()?.isCollapsed ?? true) document.execCommand('insertHTML', false, `<a href="${escapeAttribute(address)}">${escapeAttribute(address)}</a>`);
    else document.execCommand('createLink', false, address);
    setPanel(null);
    emit();
  };
  const removeLink = () => {
    restoreSelection();
    document.execCommand('unlink');
    setPanel(null);
    emit();
  };
  const insertTable = () => {
    if (tableSizeProblem(rows, columns)) return;
    restoreSelection();
    outOfCallout();
    document.execCommand('insertHTML', false, tableHtml(rows!, columns!, headerRow));
    setPanel(null);
    emit();
  };

  /** 🔒 The picture goes up as a File first, and only its id and a drawing address come back into the words (decision 0800). */
  const uploadPicture = async (picked: File | undefined) => {
    if (!picked || !onPicture) return;
    setPictureNotice(t('rte.picture.uploading'));
    const answer = await onPicture(picked);
    if ('problem' in answer) return setPictureNotice(`${t('rte.picture.failed')}: ${answer.problem}`);
    setPictureNotice(null);
    restoreSelection();
    outOfCallout();
    const alt = picked.name.replace(/\.[a-z0-9]+$/i, '');
    document.execCommand('insertHTML', false, `<img ${pictureAttribute}="${escapeAttribute(answer.fileId)}" src="${escapeAttribute(answer.src)}" alt="${escapeAttribute(alt)}">`);
    emit();
  };
  const insertImage = (picked: File | undefined) => {
    if (!picked) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') run('insertImage', reader.result);
    };
    reader.readAsDataURL(picked);
  };
  /** What each of the article's own glyph buttons does when pressed (decision 0800); read at the press, never while drawing. */
  const press = (toolName: ArticleAction) => {
    switch (toolName) {
      case 'link': return openPanel('link');
      case 'table': return openPanel('table');
      case 'horizontalRule': {
        const node = surface.current;
        node?.focus();
        if (node) caretInWords(node);
        outOfCallout();
        return run('insertHorizontalRule');
      }
    }
  };
  /** A glyph button that is not one command of the document's own: it says why where it cannot act. */
  const action = (toolName: ArticleAction, labelKey: Parameters<typeof t>[0], meaning: IconMeaning, unavailable: Parameters<typeof t>[0] | null = null) => (
    <button
      type="button"
      className={styles.tool}
      onMouseDown={keepFocus}
      onClick={() => press(toolName)}
      aria-label={t(labelKey)}
      title={unavailable ? `${t(labelKey)} · ${t(unavailable)}` : t(labelKey)}
      disabled={unavailable !== null}
      data-rich-text-tool={toolName}
    >
      <Icon meaning={meaning} size="inline" />
    </button>
  );
  const tool = (command: string, labelKey: Parameters<typeof t>[0], meaning: IconMeaning, toolName: string, argument?: string, unavailable: Parameters<typeof t>[0] | null = null) => (
    <button
      type="button"
      className={styles.tool}
      onMouseDown={keepFocus}
      onClick={() => run(command, argument)}
      aria-label={t(labelKey)}
      title={unavailable ? `${t(labelKey)} · ${t(unavailable)}` : t(labelKey)}
      disabled={unavailable !== null}
      aria-pressed={active.has(command)}
      data-rte-tool={argument ? `${command}:${argument}` : command}
      data-rich-text-tool={toolName}
      data-rte-active={active.has(command) ? 'true' : 'false'}
    >
      <Icon meaning={meaning} size="inline" />
    </button>
  );

  const numberingMenu = (
    <select className={styles.pick} aria-label={t('rte.numbering')} title={place.numbered ? t('rte.numbering') : t('rte.numbering.none')} disabled={!place.numbered} onMouseDown={saveSelection} onFocus={saveSelection} onChange={(event) => { if (event.target.value) pressNumbering(event.target.value); event.target.value = ''; }} defaultValue="" data-rte-tool="listNumbering" data-rich-text-tool="numberListMenu"><option value="" disabled>{t('rte.numbering')}</option><option value="continue">{t('rte.numbering.continue')}</option><option value="startAt">{t('rte.numbering.startAt')}</option></select>
  );

  /** The blocks the selection covers, innermost each: what line spacing and clear formatting act on (decision 1537). */
  const coveredBlocks = (): HTMLElement[] => {
    const node = surface.current;
    const selection = document.getSelection();
    if (!node || !selection || selection.rangeCount === 0 || !node.contains(selection.anchorNode)) return [];
    const range = selection.getRangeAt(0);
    const leaves = Array.from(node.querySelectorAll<HTMLElement>(BLOCKS)).filter((block) => range.intersectsNode(block) && !block.querySelector(BLOCKS));
    if (leaves.length > 0) return leaves;
    const start = range.startContainer;
    const block = (start instanceof Element ? start : start.parentElement)?.closest<HTMLElement>(BLOCKS) ?? null;
    return block && block !== node && node.contains(block) ? [block] : [];
  };
  /** 🔒 Line spacing (decision 1537): each block the selection covers takes the spacing as its `line-height`, one of the four. */
  const applyLineSpacing = (spacing: LineSpacing) => {
    restoreSelection();
    let blocks = coveredBlocks();
    if (blocks.length === 0) {
      // Words typed straight into the surface stand in no block yet; they become a paragraph first.
      document.execCommand('formatBlock', false, 'P');
      blocks = coveredBlocks();
    }
    for (const block of blocks) block.style.lineHeight = spacing;
    emit();
  };
  /**
   * 🔒 A callout from the style menu (T3572; decision 10530). With the caret in
   * one its kind changes; elsewhere the blocks the selection covers are wrapped
   * in one, and a selection reaching a table, a picture, a rule or the contents
   * is refused with the reason and moves nothing.
   */
  const applyCallout = (kind: ArticleCalloutKind) => {
    const node = surface.current;
    if (!node) return;
    node.focus();
    caretInWords(node);
    const selection = document.getSelection();
    const current = calloutAt(node, selection?.anchorNode ?? null);
    if (current) {
      current.aside.setAttribute(ARTICLE_CALLOUT_ATTRIBUTE, kind);
      emit();
      readCommandState();
      return;
    }
    const covered = () => (selection && selection.rangeCount > 0 ? coveredTopBlocks(node, selection.getRangeAt(0)) : []);
    let blocks = covered();
    if (blocks === null || blocks.length === 0) {
      // Words typed straight into the surface stand in no block yet; they become a paragraph first.
      document.execCommand('formatBlock', false, 'P');
      blocks = covered();
    }
    if (!blocks || blocks.length === 0) return;
    if (calloutRefused(blocks)) {
      setCalloutNotice(t('rte.callout.refused'));
      setAnnouncement(t('rte.callout.refused'));
      readCommandState();
      return;
    }
    const wrapped = blocks;
    keepCaret(() => {
      wrapInCallout(wrapped, kind);
      return null;
    });
  };
  /** 🔒 The style menu's pick (decisions 0750, 10530): a callout writes itself, and any other style takes the callout's words out first, Normal doing nothing more. */
  const pickStyle = (next: StyleBlock) => {
    setBlock(next);
    setCalloutNotice(null);
    const kind = CALLOUT_OF_BLOCK[next];
    if (kind) return applyCallout(kind);
    const node = surface.current;
    if (node && wordProcessor) {
      node.focus();
      caretInWords(node);
      const callout = calloutAt(node, document.getSelection()?.anchorNode ?? null);
      if (callout) {
        keepCaret(() => {
          liftCallout(callout.aside);
          return null;
        });
        if (next === 'P') return;
      }
    }
    run('formatBlock', next);
  };
  /** 🔒 A picture, a table or a rule inserted with the caret in a callout lands on a new line after it (decision 10530). */
  const outOfCallout = () => {
    const node = surface.current;
    if (!node || !wordProcessor) return;
    const line = lineAfterCallout(node, document.getSelection()?.anchorNode ?? null);
    if (!line) return;
    const range = document.createRange();
    range.setStart(line, 0);
    range.collapse(true);
    const selection = document.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  /** The quotation the caret stands in, while it is in the words. */
  const quoteAtCaret = (): HTMLElement | null => {
    const node = surface.current;
    const anchor = document.getSelection()?.anchorNode ?? null;
    const element = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
    const quote = element?.closest<HTMLElement>('blockquote') ?? null;
    return node && quote && node.contains(quote) ? quote : null;
  };
  /** 🔒 Quote is a toggle (decision 1537): the block becomes a quotation, and a press inside one lifts it back, the caret where it stood. */
  const toggleQuote = () => {
    const quote = quoteAtCaret();
    if (!quote) return run('formatBlock', 'BLOCKQUOTE');
    keepCaret(() => {
      liftQuote(quote);
      return null;
    });
  };
  /**
   * 🔒 Clear formatting (decision 1537): the document's own command takes the
   * marks, the font, the size and the colours off the selection, and the blocks
   * it covers lose their line spacing and their quotation. Lists, alignment and
   * links stay, which are the words' structure rather than their look.
   */
  const clearFormatting = () => {
    const node = surface.current;
    if (!node) return;
    // The focus comes back to the words with the selection the person made, then the command acts on it.
    saveSelection();
    restoreSelection();
    document.execCommand('removeFormat');
    const selection = document.getSelection();
    const range = selection && selection.rangeCount > 0 && node.contains(selection.anchorNode) ? selection.getRangeAt(0) : null;
    const quotes = range ? Array.from(node.querySelectorAll<HTMLElement>('blockquote')).filter((quote) => range.intersectsNode(quote)) : [];
    keepCaret(() => {
      for (const block of coveredBlocks()) {
        block.style.removeProperty('line-height');
        if (!block.getAttribute('style')) block.removeAttribute('style');
      }
      // The innermost first, so a quotation inside another is lifted before the one holding it.
      for (const quote of quotes.reverse()) if (quote.isConnected) liftQuote(quote);
      return null;
    });
  };
  /** What each word control does when pressed (decision 1537); read at the press, never while drawing. */
  const pressWord = (toolName: WordAction) => {
    switch (toolName) {
      case 'undo':
      case 'redo':
        run(toolName);
        // Where the browser does not say what its history holds, a step through it leaves both open.
        return documentHistory() ? undefined : setHistory({ undo: true, redo: true });
      case 'quote': return toggleQuote();
      case 'clearFormatting': return clearFormatting();
      case 'signature': return signature ? run('insertHTML', signature) : undefined;
    }
  };
  /** A control the icon reference draws no glyph for, drawn as its words (decision 1537). */
  const wordTool = (toolName: WordAction, labelKey: Parameters<typeof t>[0], pressed?: boolean, unavailable: Parameters<typeof t>[0] | null = null) => (
    <button
      type="button"
      className={`${styles.tool} ${styles.wordTool}`}
      onMouseDown={keepFocus}
      onClick={() => pressWord(toolName)}
      aria-label={t(labelKey)}
      title={unavailable ? `${t(labelKey)} · ${t(unavailable)}` : t(labelKey)}
      disabled={unavailable !== null}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed, 'data-rte-active': pressed ? 'true' : 'false' })}
      data-rte-tool={toolName}
      data-rich-text-tool={toolName}
    >
      {t(labelKey)}
    </button>
  );

  /**
   * 🔒 The Size menu both editors draw (decision 1615): the point ladder, each
   * size as its number and *pt*, with the size at the caret drawn selected. A
   * run in a size the ladder does not name draws the menu blank.
   */
  const sizeMenu = (
    <select
      className={styles.pick}
      aria-label={t('rte.size')}
      title={t('rte.size')}
      onMouseDown={saveSelection}
      onFocus={saveSelection}
      value={pointSize === null ? '' : String(pointSize)}
      onChange={(event) => {
        const points = Number(event.target.value);
        if ((FONT_POINT_SIZES as readonly number[]).includes(points)) applySize(points as FontPointSize);
      }}
      data-rte-tool="fontSize"
      data-rich-text-tool="fontSize"
    >
      <option value="" disabled>{t('rte.size')}</option>
      {FONT_POINT_SIZES.map((points) => (
        <option key={points} value={String(points)}>{fill(t('rte.size.points'), { n: String(points) })}</option>
      ))}
    </select>
  );

  /**
   * 🔒 The email consumers' toolbar (decision 1537): the full set in its
   * groups, in the one order a mail program draws them. Insert signature and
   * Insert Placeholder stand last, where a consumer hands either in.
   */
  const fullSet = email && !textOnly;
  const emailGroups: { key: string; node: ReactNode }[] = fullSet
    ? [
        {
          key: 'history',
          node: (
            <>
              {wordTool('undo', 'rte.undo', undefined, history.undo ? null : 'rte.undo.none')}
              {wordTool('redo', 'rte.redo', undefined, history.redo ? null : 'rte.redo.none')}
            </>
          ),
        },
        {
          key: 'font',
          node: (
            <>
              <select className={styles.pick} aria-label={t('rte.font')} onChange={(event) => { run('fontName', event.target.value); event.target.value = ''; }} defaultValue="" data-rte-tool="fontName" data-rich-text-tool="fontFamily">
                <option value="" disabled>{t('rte.font')}</option>
                {FONTS.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
              {sizeMenu}
            </>
          ),
        },
        {
          key: 'marks',
          node: (
            <>
              {tool('bold', 'rte.bold', 'bold', 'bold')}
              {tool('italic', 'rte.italic', 'italic', 'italic')}
              {tool('underline', 'rte.underline', 'underline', 'underline')}
              {tool('strikeThrough', 'rte.strike', 'strikethrough', 'strikethrough')}
            </>
          ),
        },
        {
          key: 'colours',
          node: (
            <>
              <label className={styles.colorTool} title={t('rte.textColor')} data-rich-text-tool="textColor">
                <Icon meaning="textColor" size="inline" />
                <input type="color" className={styles.color} aria-label={t('rte.textColor')} onChange={(event) => run('foreColor', event.target.value)} data-rte-tool="foreColor" />
              </label>
              <label className={styles.colorTool} title={t('rte.highlight')} data-rich-text-tool="highlightColor">
                <Icon meaning="highlight" size="inline" />
                <input type="color" className={styles.color} aria-label={t('rte.highlight')} onChange={(event) => run('hiliteColor', event.target.value)} data-rte-tool="hiliteColor" />
              </label>
            </>
          ),
        },
        ...(alignment
          ? [{
              key: 'alignment',
              node: (
                <>
                  {tool('justifyLeft', 'rte.alignLeft', 'alignLeft', 'alignLeft')}
                  {tool('justifyCenter', 'rte.alignCenter', 'alignCenter', 'alignCenter')}
                  {tool('justifyRight', 'rte.alignRight', 'alignRight', 'alignRight')}
                  {tool('justifyFull', 'rte.alignJustify', 'alignJustify', 'alignJustify')}
                </>
              ),
            }]
          : []),
        { key: 'lists', node: <>{tool('insertUnorderedList', 'rte.bullets', 'bulletList', 'bulletList')}{tool('insertOrderedList', 'rte.numbers', 'numberedList', 'numberList')}{wordProcessor ? null : numberingMenu}</> },
        { key: 'indent', node: <>{tool('outdent', 'rte.outdent', 'outdent', 'outdent', undefined, place.indented ? null : 'rte.outdent.none')}{tool('indent', 'rte.indent', 'indent', 'indent')}</> },
        {
          key: 'spacing',
          node: (
            <select className={styles.pick} aria-label={t('rte.lineSpacing')} title={t('rte.lineSpacing')} onMouseDown={saveSelection} onFocus={saveSelection} onChange={(event) => { if (event.target.value) applyLineSpacing(event.target.value as LineSpacing); event.target.value = ''; }} defaultValue="" data-rte-tool="lineHeight" data-rich-text-tool="lineSpacing">
              <option value="" disabled>{t('rte.lineSpacing')}</option>
              {LINE_SPACINGS.map((spacing) => (
                <option key={spacing} value={spacing}>{spacing}</option>
              ))}
            </select>
          ),
        },
        {
          key: 'insert',
          node: (
            <>
              {action('link', 'rte.link', 'link')}
              {pictures ? (
                <button type="button" className={styles.tool} onMouseDown={keepFocus} onClick={() => { saveSelection(); file.current?.click(); }} aria-label={t('rte.image')} title={t('rte.image')} data-rte-tool="insertImage" data-rich-text-tool="image">
                  <Icon meaning="image" size="inline" />
                </button>
              ) : null}
              {action('table', 'rte.table', 'table')}
            </>
          ),
        },
        { key: 'quote', node: wordTool('quote', 'rte.quote', place.quote) },
        { key: 'clear', node: wordTool('clearFormatting', 'rte.clearFormatting', undefined, place.formatted ? null : 'rte.clearFormatting.none') },
        ...(signature ? [{ key: 'signature', node: wordTool('signature', 'rte.signature') }] : []),
        ...(placeholders.length > 0
          ? [{
              key: 'placeholder',
              node: (
                <span className={styles.pickWrap} title={t('rte.placeholder')}>
                  <Icon meaning="mergeField" size="inline" />
                  <select className={styles.pick} aria-label={t('rte.placeholder')} onChange={(event) => { if (event.target.value) run('insertHTML', mergeFieldHtml(event.target.value)); event.target.value = ''; }} defaultValue="" data-rte-tool="insertPlaceholder" data-rich-text-tool="placeholder">
                    <option value="" disabled>{t('rte.placeholder')}</option>
                    {placeholders.map((field) => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </select>
                </span>
              ),
            }]
          : []),
      ]
    : [];
  const kept = emailGroups.length - Math.min(folded, emailGroups.length);
  // The More menu is open only while something is folded behind it.
  const menuShown = moreOpen && kept < emailGroups.length;
  const drawnKeys = emailGroups.map((group) => group.key).join(' ');
  // 🔒 The fold is measured whenever the groups or the fold change (decision 1537), and the resize observer measures again whenever the row does.
  useLayoutEffect(() => {
    if (!fullSet) return;
    groupKeys.current = drawnKeys.split(' ');
    measureFold();
  }, [fullSet, drawnKeys, folded, measureFold]);
  useEffect(() => {
    const row = toolbar.current;
    if (!fullSet || !row) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureFold);
      return () => window.removeEventListener('resize', measureFold);
    }
    const observer = new ResizeObserver(() => measureFold());
    observer.observe(row);
    return () => observer.disconnect();
  }, [fullSet, measureFold]);
  // The More menu closes on a press anywhere outside it and its button.
  useEffect(() => {
    if (!menuShown) return undefined;
    const away = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (target && (overflow.current?.contains(target) || toolbar.current?.querySelector('[data-rich-text-more]')?.contains(target))) return;
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [menuShown]);

  return (
    <div className={styles.editor} data-rich-text-editor={textOnly ? 'text' : email ? 'email' : 'core'} data-rich-text={name ?? (email ? 'email' : 'core')} data-name={name}>
      <span className={labelHidden ? styles.labelHidden : styles.label} id={`${id}-label`}>{label}</span>
      {/*
        🔒 The article editor's toolbar and object bar stick to the top of the page's one scrolling region while the words scroll under them (T555; decision 1273).
        The email consumers keep them in the editor's own flow, drawn exactly as before.
      */}
      <div className={wordProcessor ? styles.stickyHead : styles.head} data-rich-text-head={wordProcessor ? 'sticky' : 'inline'}>
      {fullSet ? (
        <div ref={toolbar} className={`${styles.toolbar} ${styles.toolbarOneRow}`} role="toolbar" aria-label={t('rte.toolbar')} aria-controls={id} data-rich-text-folded={Math.min(folded, emailGroups.length)}>
          {emailGroups.slice(0, kept).map((group, index) => (
            <span key={group.key} className={styles.group} data-rich-text-group={group.key} data-rich-text-group-index={index}>
              {group.node}
            </span>
          ))}
          {kept < emailGroups.length ? (
            // 🔒 The groups the row has no room for stand behind one More control, never on a second row (decision 1537).
            <button
              type="button"
              className={`${styles.tool} ${styles.more}`}
              onMouseDown={keepFocus}
              onClick={() => setMoreOpen((open) => !open)}
              aria-label={t('rte.more')}
              title={t('rte.more')}
              aria-haspopup="true"
              aria-expanded={menuShown}
              data-rich-text-more="true"
            >
              <Icon meaning="moreActions" size="inline" />
            </button>
          ) : null}
          {menuShown ? (
            <div
              ref={overflow}
              className={styles.overflow}
              role="group"
              aria-label={t('rte.more')}
              data-rich-text-overflow="true"
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return;
                event.preventDefault();
                setMoreOpen(false);
                toolbar.current?.querySelector<HTMLElement>('[data-rich-text-more]')?.focus();
              }}
            >
              {emailGroups.slice(kept).map((group) => (
                <span key={group.key} className={styles.group} data-rich-text-group={group.key}>
                  {group.node}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
      <div className={styles.toolbar} role="toolbar" aria-label={t('rte.toolbar')} aria-controls={id}>
        <span className={styles.group}>
          {styleMenu ? (
            // 🔒 The article's style menu (decision 0750): Normal, Heading 1 and Heading 2, applied to the block the caret stands in.
            <select className={styles.pick} aria-label={t('rte.style')} value={block} onChange={(event) => pickStyle(event.target.value as StyleBlock)} data-rte-tool="formatBlock" data-rich-text-tool="headingStyle">
              {STYLE_BLOCKS.map((option) => (
                <option key={option.block} value={option.block}>{t(option.key)}</option>
              ))}
            </select>
          ) : (
          /* The heading is a toggle, a heading block or a paragraph, the way the acceptance suite and the shared rule read it (piece 5). */
          <button
            type="button"
            className={styles.tool}
            onMouseDown={keepFocus}
            onClick={() => run('formatBlock', heading ? 'P' : 'H2')}
            aria-label={t('rte.heading')}
            title={t('rte.heading')}
            aria-pressed={heading}
            data-rte-tool="formatBlock"
            data-rich-text-tool="heading"
            data-rte-active={heading ? 'true' : 'false'}
          >
            <Icon meaning="heading" size="inline" />
          </button>
          )}
        </span>
        <span className={styles.group}>
          {tool('bold', 'rte.bold', 'bold', 'bold')}
          {tool('italic', 'rte.italic', 'italic', 'italic')}
          {tool('underline', 'rte.underline', 'underline', 'underline')}
          {tool('strikeThrough', 'rte.strike', 'strikethrough', 'strikethrough')}
        </span>
        <span className={styles.group}>
          {tool('insertUnorderedList', 'rte.bullets', 'bulletList', 'bulletList')}
          {tool('insertOrderedList', 'rte.numbers', 'numberedList', 'numberList')}
          {wordProcessor ? null : numberingMenu}
        </span>
        {wordProcessor ? (
          <span className={styles.group} data-rich-text-group="marks">
            {sizeMenu}
            <label className={styles.colorTool} title={t('rte.textColor')} data-rich-text-tool="textColor">
              <Icon meaning="textColor" size="inline" />
              <input type="color" className={styles.color} aria-label={t('rte.textColor')} onChange={(event) => runStyled('foreColor', event.target.value)} data-rte-tool="foreColor" />
            </label>
            <label className={styles.colorTool} title={t('rte.highlight')} data-rich-text-tool="highlightColor">
              <Icon meaning="highlight" size="inline" />
              <input type="color" className={styles.color} aria-label={t('rte.highlight')} onChange={(event) => runStyled('hiliteColor', event.target.value)} data-rte-tool="hiliteColor" />
            </label>
          </span>
        ) : null}
        {alignment ? (
          <span className={styles.group} data-rich-text-group="alignment">
            {tool('justifyLeft', 'rte.alignLeft', 'alignLeft', 'alignLeft')}
            {tool('justifyCenter', 'rte.alignCenter', 'alignCenter', 'alignCenter')}
            {tool('justifyRight', 'rte.alignRight', 'alignRight', 'alignRight')}
            {tool('justifyFull', 'rte.alignJustify', 'alignJustify', 'alignJustify')}
          </span>
        ) : null}
        {textOnly ? null : (
          <>
            {wordProcessor ? (
              <span className={styles.group} data-rich-text-group="insert">
                {action('link', 'rte.link', 'link')}
                {action('table', 'rte.table', 'table')}
                {action('horizontalRule', 'rte.rule', 'horizontalRule')}
              </span>
            ) : null}
            {pictures ? (
              <span className={styles.group}>
                <button type="button" className={styles.tool} onMouseDown={keepFocus} onClick={() => { saveSelection(); file.current?.click(); }} aria-label={t('rte.image')} title={t('rte.image')} data-rte-tool="insertImage" data-rich-text-tool="image">
                  <Icon meaning="image" size="inline" />
                </button>
                <input ref={file} type="file" accept={(pictureTypes ?? ['image/*']).join(',')} className={styles.hiddenFile} tabIndex={-1} aria-hidden="true" data-rich-text-picture-input="true" onChange={(event) => { const picked = event.target.files?.[0]; if (onPicture) void uploadPicture(picked); else insertImage(picked); event.target.value = ''; }} />
              </span>
            ) : null}
            {inserts.length > 0 ? (
              <span className={styles.group}>
                {inserts.map((insert) => (
                  <button key={insert.key} type="button" className={styles.tool} onMouseDown={keepFocus} onClick={() => run('insertHTML', insert.html(surface.current?.innerHTML ?? ''))} aria-label={insert.label} title={insert.label} data-rte-tool={`insert:${insert.key}`} data-rich-text-tool={insert.key}>
                    <Icon meaning={insert.icon} size="inline" />
                  </button>
                ))}
              </span>
            ) : null}
          </>
        )}
      </div>
      )}
      {/* 🔒 The held object's bar, docked under the toolbar at every width (decision 1240); the article's alone. */}
      {wordProcessor && shown?.kind === 'picture' ? (
        <PictureBar facts={shown.facts} onSize={onPictureSize} onAlign={onPictureAlign} onAlt={onPictureAlt} onCaption={onPictureCaption} onMinWidth={onPictureMinWidth} onRemove={onPictureRemove} onEscape={restoreSelection} phone={phone} barRef={bar} />
      ) : null}
      {wordProcessor && shown?.kind === 'list' ? <ListBar facts={shown.facts} onAction={onList} onStart={onListStart} onEscape={restoreSelection} phone={phone} barRef={bar} /> : null}
      {wordProcessor && shown?.kind === 'table' ? <TableBar facts={shown.facts} onAction={onTable} onEscape={restoreSelection} asking={asking} onConfirmDelete={confirmDelete} onKeep={keepTable} phone={phone} barRef={bar} /> : null}
      {wordProcessor ? (
        <span className={styles.announce} aria-live="polite" data-rich-text-announce="true">
          {announcement}
        </span>
      ) : null}
      </div>
      {panel === 'link' ? (
        <div className={styles.panel} data-rich-text-panel="link">
          <TextInput size={Size.Medium} label={t('rte.link.address')} value={linkTyped} onChange={(typed) => { setLinkTyped(typed); setLinkProblem(null); }} error={linkProblem} name="rteLinkAddress" placeholder={t('rte.link.placeholder')} />
          <span className={styles.panelActions}>
            <Button size={Size.Small} color={Color.Navy} text={[t('rte.link.apply')]} actions={[applyLink]} disabled={linkTyped.trim() === ''} disabledReason={t('rte.link.problem')} />
            {place.link ? <Button size={Size.Small} text={[t('rte.link.remove')]} actions={[removeLink]} /> : null}
            <Button size={Size.Small} color={Color.Inactive} text={[t('rte.panelCancel')]} actions={[() => setPanel(null)]} />
          </span>
        </div>
      ) : null}
      {panel === 'listStart' ? (
        <div className={styles.panel} data-rich-text-panel="listStart">
          <NumericInput size={Size.Small} kind="number" decimals={0} label={t('rte.numbering.startLabel')} value={listStart} onChange={setListStartTyped} name="rteListStartAt" chars={3} />
          <span className={styles.panelActions}>
            <Button size={Size.Small} color={Color.Navy} text={[t('rte.numbering.apply')]} actions={[applyListStart]} disabled={listStart === null || listStart < LIST_START_MIN || listStart > LIST_START_MAX} disabledReason={t('rte.numbering.startRefused')} />
            <Button size={Size.Small} color={Color.Inactive} text={[t('rte.panelCancel')]} actions={[() => setPanel(null)]} />
          </span>
        </div>
      ) : null}
      {numberingNotice ? <p className={styles.notice} role="status" data-rich-text-numbering-notice="true">{numberingNotice}</p> : null}
      {panel === 'table' ? (
        <div className={styles.panel} data-rich-text-panel="table">
          <NumericInput size={Size.Small} kind="number" decimals={0} label={t('rte.table.rows')} value={rows} onChange={setRows} name="rteTableRows" chars={3} />
          <NumericInput size={Size.Small} kind="number" decimals={0} label={t('rte.table.columns')} value={columns} onChange={setColumns} name="rteTableColumns" chars={3} />
          <Checkbox label={t('rte.table.header')} checked={headerRow} onChange={setHeaderRow} size={Size.Small} name="rteTableHeader" />
          <span className={styles.panelActions}>
            <Button size={Size.Small} color={Color.Navy} text={[t('rte.table.insert')]} actions={[insertTable]} disabled={tableSizeProblem(rows, columns)} disabledReason={t('rte.table.problem')} />
            <Button size={Size.Small} color={Color.Inactive} text={[t('rte.panelCancel')]} actions={[() => setPanel(null)]} />
          </span>
        </div>
      ) : null}
      {fullSet && pictures ? (
        // The email toolbar's picture input stands outside its groups, so a group folded into More still has the input it opened.
        <input ref={file} type="file" accept={(pictureTypes ?? ['image/*']).join(',')} className={styles.hiddenFile} tabIndex={-1} aria-hidden="true" data-rich-text-picture-input="true" onChange={(event) => { const picked = event.target.files?.[0]; if (onPicture) void uploadPicture(picked); else insertImage(picked); event.target.value = ''; }} />
      ) : null}
      {pictureNotice ? <p className={styles.notice} role="status" data-rich-text-picture-notice="true">{pictureNotice}</p> : null}
      {calloutNotice ? <p className={styles.notice} role="status" data-rich-text-callout-notice="true">{calloutNotice}</p> : null}
      <div
        ref={surface}
        id={id}
        className={[styles.surface, wordProcessor ? styles.surfaceGrows : '', (wordProcessor || email) && !textOnly ? styles.surfaceSized : ''].filter(Boolean).join(' ')}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-labelledby={`${id}-label`}
        spellCheck
        data-rich-text-surface="true"
        data-placeholder={placeholder ?? ''}
        onInput={(event) => {
          settlePendingSize();
          emit(isHistoryInput(event.nativeEvent) ? 'history' : 'edit');
        }}
        onBlur={() => {
          pendingSize.current = null;
          pendingAt.current = null;
          emit();
        }}
        onKeyDown={onSurfaceKeyDown}
        onPaste={onSurfacePaste}
        onMouseDown={onSurfaceMouseDown}
        suppressContentEditableWarning
      />
    </div>
  );
}

/** The read view of what the editor wrote: the one surface's own markup, drawn as it was composed (piece 5). */
export function RichTextView({ html, sanitize = sanitizeRichText, className, ...rest }: { html: string; sanitize?: (html: string) => string; className?: string | undefined; 'data-rich-text-view'?: string }) {
  // \u{1F528} Sanitised here and not only where it was stored (decision 0360). The
  // markup reaching this component is the editor's own today, but the component
  // does not know that and must not depend on it: the allowlist runs on every
  // render, so a second consumer cannot introduce a hole by existing. A consumer
  // with its own allowed table names it here (decision 0800); none goes without one.
  // 🔨 Cleaned once per body and per allowance, not on every render (T544).
  const clean = useMemo(() => sanitize(html), [sanitize, html]);
  return <div className={className ? `${styles.view} ${className}` : styles.view} dangerouslySetInnerHTML={{ __html: clean }} {...rest} />;
}
